import type { UseQueryResult } from '@tanstack/react-query';
import { useDemoMode } from './context.js';

/**
 * Common "result envelope" — matches the shape every customer-spa
 * data hook returns today (data + isLoading + error + refetch).
 * Each demo fixture short-circuits the live query with a synthetic
 * envelope that looks resolved (`isLoading=false`, `error=null`).
 */
export interface QueryEnvelope<T> {
  data?: T;
  isLoading: boolean;
  isFetching?: boolean;
  error: Error | null;
  refetch?: () => void;
}

/**
 * Swap a live TanStack-Query result with a demo fixture when demo
 * mode is on. Hooks that opt in look like:
 *
 *   const live = useQuery({ ... });
 *   return useDemoOrLive(demoFixture, toEnvelope(live));
 *
 * The helper preserves the `isFetching` + `refetch` fields on the
 * live envelope so consumers that expose a refresh button keep
 * working in live mode. In demo mode `refetch` is a no-op (the
 * fixture doesn't change) and `isFetching` is always `false`.
 */
export function useDemoOrLive<T>(demoFixture: T, live: QueryEnvelope<T>): QueryEnvelope<T> {
  const { isDemoMode } = useDemoMode();
  if (!isDemoMode) return live;
  return {
    data: demoFixture,
    isLoading: false,
    isFetching: false,
    error: null,
    refetch: () => undefined,
  };
}

/**
 * Adapter: TanStack-Query's `UseQueryResult` carries a few extra
 * fields the hooks don't surface (`status`, `isSuccess`, etc.). The
 * customer-spa hooks all narrow to a `QueryEnvelope` shape before
 * returning — this helper does the same narrowing so the demo-vs-live
 * swap stays type-clean.
 */
export function toEnvelope<T>(q: UseQueryResult<T>): QueryEnvelope<T> {
  return {
    data: q.data,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    error: (q.error as Error | null) ?? null,
    refetch: () => void q.refetch(),
  };
}
